import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { unwrapAsyncResult } from '../lib/asyncOperation'
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist'
import { useToast } from '../ui/ToastProvider'
import MenuGuideButton from '../components/MenuGuideButton'

// pdf.js worker 설정
const PDFJS_WORKER_URL = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString()
GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL

function isPdfJsWorkerLoadError(e) {
  const msg = String(e?.message || e || '')
  return (
    msg.includes('Setting up fake worker failed') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Failed to load module script') ||
    msg.includes('Strict MIME type checking')
  )
}

async function getDocumentWithWorkerFallback(opts) {
  let loadingTask = null
  try {
    loadingTask = getDocument(opts)
  } catch (e) {
    if (isPdfJsWorkerLoadError(e)) {
      const loadingTask2 = getDocument({ ...opts, disableWorker: true })
      const pdf2 = await loadingTask2.promise
      return { loadingTask: loadingTask2, pdf: pdf2, usedFallback: true }
    }
    throw e
  }
  try {
    const pdf = await loadingTask.promise
    return { loadingTask, pdf, usedFallback: false }
  } catch (e) {
    try { loadingTask.destroy?.() } catch { }
    if (!isPdfJsWorkerLoadError(e)) throw e
    const loadingTask2 = getDocument({ ...opts, disableWorker: true })
    const pdf2 = await loadingTask2.promise
    return { loadingTask: loadingTask2, pdf: pdf2, usedFallback: true }
  }
}

function PdfViewer({ title, pdfBytes, pageNum, setPageNum }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [pageInfo, setPageInfo] = useState({ width: 0, height: 0, scale: 1.0 })
  const [numPages, setNumPages] = useState(0)
  const [pdfDoc, setPdfDoc] = useState(null)

  const canvasId = useMemo(() => `terms-pdf-${Math.random().toString(16).slice(2)}`, [])

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!pdfBytes) {
        setPdfDoc(null)
        setNumPages(0)
        setPageNum(1)
        return
      }
      setBusy(true)
      setErr('')
      try {
        const bytes = (pdfBytes instanceof ArrayBuffer) ? pdfBytes.slice(0) : pdfBytes
        const { loadingTask, pdf } = await getDocumentWithWorkerFallback({ data: bytes })
        if (cancelled) {
          try { loadingTask.destroy?.() } catch { }
          try { pdf.destroy?.() } catch { }
          return
        }
        setPdfDoc(pdf)
        setNumPages(pdf.numPages || 0)
        setPageNum(1)
      } catch (e) {
        console.error(e)
        if (!cancelled) setErr('PDF를 열 수 없습니다.')
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfBytes])

  useEffect(() => {
    return () => {
      try { pdfDoc?.destroy?.() } catch { }
    }
  }, [pdfDoc])

  useEffect(() => {
    let cancelled = false
    async function render() {
      if (!pdfDoc) return
      setBusy(true)
      setErr('')
      try {
        const n = pdfDoc.numPages || 0
        const safe = Math.max(1, Math.min(n || 1, pageNum || 1))
        if (safe !== pageNum) setPageNum(safe)
        const page = await pdfDoc.getPage(safe)
        const viewport = page.getViewport({ scale: pageInfo.scale || 1.0 })
        if (cancelled) return
        const canvas = document.getElementById(canvasId)
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        canvas.width = Math.floor(viewport.width)
        canvas.height = Math.floor(viewport.height)
        setPageInfo((p) => ({ ...p, width: canvas.width, height: canvas.height }))
        const task = page.render({ canvasContext: ctx, viewport })
        await task.promise
      } catch (e) {
        console.error(e)
        if (!cancelled) setErr('렌더링에 실패했습니다.')
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    render()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, pageNum, pageInfo.scale, canvasId])

  return (
    <div className="rounded border p-3 space-y-2 bg-white">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-slate-500">{busy ? '렌더링 중…' : (pageInfo.width ? `${pageInfo.width}×${pageInfo.height}px` : '')}</div>
      </div>
      {err && <div className="text-xs text-rose-600">{err}</div>}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <button type="button" className="rounded border px-2 py-1 text-sm disabled:opacity-50" disabled={!numPages || pageNum <= 1 || busy} onClick={() => setPageNum(p => Math.max(1, (p || 1) - 1))}>이전</button>
        <div className="text-sm">
          <input className="w-14 rounded border px-2 py-1 text-sm" value={pageNum} onChange={(e) => setPageNum(Number(e.target.value || 1))} disabled={busy || !numPages} /> / {numPages || '—'}
        </div>
        <button type="button" className="rounded border px-2 py-1 text-sm disabled:opacity-50" disabled={!numPages || pageNum >= numPages || busy} onClick={() => setPageNum(p => Math.min(numPages || 1, (p || 1) + 1))}>다음</button>
        <span className="text-xs text-slate-500">확대</span>
        <select className="rounded border px-2 py-1 text-sm" value={pageInfo.scale} onChange={(e) => setPageInfo((p) => ({ ...p, scale: Number(e.target.value) }))} disabled={busy}>
          <option value={1.0}>100%</option>
          <option value={1.25}>125%</option>
          <option value={1.5}>150%</option>
          <option value={2.0}>200%</option>
        </select>
      </div>
      <div className="rounded border overflow-auto">
        <canvas id={canvasId} className="block" />
      </div>
    </div>
  )
}

export default function TermsLibraryPage() {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [insurers, setInsurers] = useState([])
  const [products, setProducts] = useState([])
  const [versions, setVersions] = useState([])

  const [insurerId, setInsurerId] = useState('')
  const [productId, setProductId] = useState('')
  const [versionId, setVersionId] = useState('')
  const [searchQ, setSearchQ] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [collectionSources, setCollectionSources] = useState([])
  const [collectionRuns, setCollectionRuns] = useState([])
  const [collectionItems, setCollectionItems] = useState([])
  const [collectionSourceId, setCollectionSourceId] = useState('')

  const [pdfBytes, setPdfBytes] = useState(null)
  const [pageNum, setPageNum] = useState(1)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setBusy(true)
      try {
        const res = await api.get('/insurers')
        const data = (res?.data && res.data.data) ? res.data.data : res.data
        const items = data?.items || []
        if (!cancelled) setInsurers(Array.isArray(items) ? items : [])
        if (!cancelled) {
          loadCollectionSources()
          loadCollectionRuns()
          loadCollectionItems()
        }
      } catch (e) {
        console.error(e)
        toast?.show?.('보험사 목록을 불러오지 못했습니다.', 'error')
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let cancelled = false
    async function loadProducts() {
      setProducts([])
      setProductId('')
      setVersions([])
      setVersionId('')
      setPdfBytes(null)
      if (!insurerId) return
      setBusy(true)
      try {
        const res = await api.get('/insurance-products', { params: { insurerId: Number(insurerId) } })
        const data = (res?.data && res.data.data) ? res.data.data : res.data
        const items = data?.items || []
        if (!cancelled) setProducts(Array.isArray(items) ? items : [])
      } catch (e) {
        console.error(e)
        toast?.show?.('상품 목록을 불러오지 못했습니다.', 'error')
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    loadProducts()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurerId])

  useEffect(() => {
    let cancelled = false
    async function loadVersions() {
      setVersions([])
      setVersionId('')
      setPdfBytes(null)
      if (!productId) return
      setBusy(true)
      try {
        const res = await api.get('/terms/versions', { params: { productId: Number(productId) } })
        const data = (res?.data && res.data.data) ? res.data.data : res.data
        const items = data?.items || []
        if (!cancelled) setVersions(Array.isArray(items) ? items : [])
      } catch (e) {
        console.error(e)
        toast?.show?.('약관 버전 목록을 불러오지 못했습니다.', 'error')
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    loadVersions()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId])

  useEffect(() => {
    let cancelled = false
    async function loadPdf() {
      setPdfBytes(null)
      setPageNum(1)
      if (!versionId) return
      setBusy(true)
      try {
        const res = await api.get(`/terms/versions/${Number(versionId)}/pdf`, { responseType: 'arraybuffer' })
        if (!cancelled) setPdfBytes(res?.data || null)
      } catch (e) {
        console.error(e)
        toast?.show?.('약관 PDF를 불러오지 못했습니다.', 'error')
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    loadPdf()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId])

  const runMetadataSearch = async () => {
    setBusy(true)
    try {
      const res = await api.get('/terms/versions/search', { params: { q: searchQ, limit: 80 } })
      const data = (res?.data && res.data.data) ? res.data.data : res.data
      setSearchResults(Array.isArray(data?.items) ? data.items : [])
    } catch (e) {
      console.error(e)
      toast?.show?.('약관 검색에 실패했습니다.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const loadCollectionSources = async () => {
    try {
      const res = await api.get('/operator/terms/collection/sources', { __silent: true })
      const data = (res?.data && res.data.data) ? res.data.data : res.data
      setCollectionSources(Array.isArray(data?.items) ? data.items : [])
    } catch {
      setCollectionSources([])
    }
  }

  const loadCollectionRuns = async () => {
    try {
      const res = await api.get('/operator/terms/collection/runs', { __silent: true })
      const data = (res?.data && res.data.data) ? res.data.data : res.data
      setCollectionRuns(Array.isArray(data?.items) ? data.items : [])
    } catch {
      setCollectionRuns([])
    }
  }

  const loadCollectionItems = async (runId = null) => {
    try {
      const params = runId ? { runId } : { status: 'NEEDS_REVIEW' }
      const res = await api.get('/operator/terms/collection/items', { params, __silent: true })
      const data = (res?.data && res.data.data) ? res.data.data : res.data
      setCollectionItems(Array.isArray(data?.items) ? data.items : [])
    } catch {
      setCollectionItems([])
    }
  }

  const seedCollectionSources = async () => {
    setBusy(true)
    try {
      await api.post('/operator/terms/collection/sources/seed-defaults')
      await loadCollectionSources()
      toast?.show?.('대표 보험사 수집 소스를 준비했습니다.', 'success')
    } catch (e) {
      console.error(e)
      toast?.show?.('수집 소스 준비에 실패했습니다.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const runTermsCollection = async () => {
    setBusy(true)
    try {
      const params = collectionSourceId ? { sourceId: Number(collectionSourceId) } : {}
      const res = await api.post('/operator/terms/collection/runs', null, { params })
      const data = await unwrapAsyncResult(res)
      await loadCollectionRuns()
      await loadCollectionItems()
      toast?.show?.(`약관 수집을 완료했습니다. (${data?.count || 0}개 소스)`, 'success')
    } catch (e) {
      console.error(e)
      toast?.show?.('약관 수집 실행에 실패했습니다.', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-lg font-bold">약관 라이브러리</div>
            <MenuGuideButton slug="terms-library" title="약관 라이브러리 가이드" />
          </div>
          <div className="text-xs text-slate-500">운영자 전용 테스트 기능입니다. 현재 검색은 등록된 약관 메타데이터 기준이며, 약관 원문 전문검색은 텍스트 인덱싱 이후 제공합니다.</div>
        </div>
        <div className="text-xs text-slate-500">{busy ? '처리 중…' : ''}</div>
      </div>

      <div className="rounded border border-amber-100 bg-amber-50/60 p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-amber-950">약관 크롤러 어댑터</div>
            <p className="mt-1 text-xs text-amber-900/80">
              대표 보험사 상품공시 페이지에서 약관 PDF/동적 API 후보를 수집하고, 승인 전 검수 큐에 적재합니다.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="rounded border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium" disabled={busy} onClick={seedCollectionSources}>
              대표 소스 준비
            </button>
            <button type="button" className="rounded bg-amber-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50" disabled={busy} onClick={runTermsCollection}>
              수집 실행
            </button>
          </div>
        </div>
        <div className="mt-3 grid gap-3 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-amber-950">수집 소스</label>
            <select className="w-full rounded border border-amber-200 bg-white px-2 py-2 text-xs" value={collectionSourceId} onChange={(e) => setCollectionSourceId(e.target.value)}>
              <option value="">전체 대표 소스</option>
              {collectionSources.map((s) => (
                <option key={s.id} value={s.id}>{s.insurerCode} · {s.sourceName}</option>
              ))}
            </select>
          </div>
          <div className="rounded border border-amber-200 bg-white p-2 text-xs">
            <div className="font-semibold text-slate-700">최근 실행</div>
            <div className="mt-1 max-h-24 overflow-auto space-y-1">
              {collectionRuns.slice(0, 5).map((r) => (
                <button key={r.id} type="button" className="block w-full text-left hover:underline" onClick={() => loadCollectionItems(r.id)}>
                  #{r.id} {r.insurerCode} · {r.status} · 후보 {r.discoveredCount || 0}
                </button>
              ))}
              {!collectionRuns.length ? <div className="text-slate-400">실행 이력이 없습니다.</div> : null}
            </div>
          </div>
          <div className="rounded border border-amber-200 bg-white p-2 text-xs">
            <div className="font-semibold text-slate-700">검수 대기</div>
            <div className="mt-1 text-slate-600">{collectionItems.length}건</div>
            <button type="button" className="mt-2 rounded border px-2 py-1 text-xs" onClick={() => loadCollectionItems()} disabled={busy}>새로고침</button>
          </div>
        </div>
        {collectionItems.length ? (
          <div className="mt-3 max-h-72 overflow-auto rounded border bg-white">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2">보험사</th>
                  <th className="px-3 py-2">상태</th>
                  <th className="px-3 py-2">약관/상품</th>
                  <th className="px-3 py-2">URL</th>
                  <th className="px-3 py-2">비고</th>
                </tr>
              </thead>
              <tbody>
                {collectionItems.map((item) => (
                  <tr key={item.id} className="border-t align-top">
                    <td className="px-3 py-2">{item.insurerCode}</td>
                    <td className="px-3 py-2">{item.collectedStatus}</td>
                    <td className="px-3 py-2">
                      <div>{item.termsTitle || '-'}</div>
                      <div className="text-slate-400">{item.productName || ''}</div>
                    </td>
                    <td className="px-3 py-2">
                      {item.pdfUrl || item.sourceUrl ? (
                        <a className="break-all text-blue-700 hover:underline" href={item.pdfUrl || item.sourceUrl} target="_blank" rel="noreferrer">열기</a>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2 max-w-md text-slate-600">{item.note || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="rounded border border-indigo-100 bg-indigo-50/60 p-3">
        <div className="text-sm font-semibold text-indigo-950">통합 약관 메타 검색</div>
        <p className="mt-1 text-xs text-indigo-900/80">
          약관 데이터 확보 전이라도, 운영자가 등록한 보험사/상품/약관 버전 메타데이터에서 검색할 수 있습니다.
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input
            className="min-w-0 flex-1 rounded border border-indigo-200 bg-white px-3 py-2 text-sm"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runMetadataSearch()
            }}
            placeholder="보험사명, 상품명, 약관 제목 검색"
          />
          <button
            type="button"
            className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={busy}
            onClick={runMetadataSearch}
          >
            검색
          </button>
        </div>
        {searchResults.length > 0 ? (
          <div className="mt-3 max-h-72 overflow-auto rounded border bg-white">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2">보험사</th>
                  <th className="px-3 py-2">상품</th>
                  <th className="px-3 py-2">약관</th>
                  <th className="px-3 py-2">시작일</th>
                  <th className="px-3 py-2">열기</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-2">{r.insurerName || '-'}</td>
                    <td className="px-3 py-2">{r.productName || '-'}</td>
                    <td className="px-3 py-2">{r.title || '약관'}</td>
                    <td className="px-3 py-2">{r.effectiveFrom || '-'}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                        onClick={() => {
                          setVersionId(r.id ? String(r.id) : '')
                        }}
                      >
                        선택
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded border bg-white p-3 space-y-2">
          <div className="text-sm font-semibold">1) 보험사</div>
          <select className="w-full rounded border px-2 py-2 text-sm" value={insurerId} onChange={(e) => setInsurerId(e.target.value)}>
            <option value="">선택</option>
            {insurers.map((i) => (
              <option key={i.id} value={i.id}>{i.displayName || i.name || i.code}</option>
            ))}
          </select>
        </div>

        <div className="rounded border bg-white p-3 space-y-2">
          <div className="text-sm font-semibold">2) 상품</div>
          <select className="w-full rounded border px-2 py-2 text-sm" value={productId} onChange={(e) => setProductId(e.target.value)} disabled={!insurerId}>
            <option value="">{insurerId ? '선택' : '보험사를 먼저 선택'}</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
            ))}
          </select>
        </div>

        <div className="rounded border bg-white p-3 space-y-2">
          <div className="text-sm font-semibold">3) 약관 버전</div>
          <select className="w-full rounded border px-2 py-2 text-sm" value={versionId} onChange={(e) => setVersionId(e.target.value)} disabled={!productId}>
            <option value="">{productId ? '선택' : '상품을 먼저 선택'}</option>
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                {(v.title || '약관')} {v.effectiveFrom ? `(${v.effectiveFrom}~)` : ''} [{v.status}]
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div>
          <PdfViewer title="약관 원문" pdfBytes={pdfBytes} pageNum={pageNum} setPageNum={setPageNum} />
        </div>
        <div className="rounded border bg-white p-3 space-y-2">
          <div className="text-sm font-semibold">다운로드</div>
          <div className="text-xs text-slate-500">현재 선택된 버전의 원문 파일을 다운로드합니다.</div>
          <button
            type="button"
            className="rounded bg-slate-900 text-white px-4 py-2 text-sm disabled:opacity-50"
            disabled={!versionId || busy}
            onClick={() => {
              if (!versionId) return
              // 새 탭 다운로드(서버에서 content-disposition을 별도로 주지 않으므로 브라우저 기본 동작)
              window.open(`/v1/terms/versions/${Number(versionId)}/pdf`, '_blank', 'noopener,noreferrer')
            }}
          >
            PDF 열기/다운로드
          </button>
        </div>
      </div>
    </div>
  )
}

